import React, { useMemo, useCallback, useEffect, useState } from "react";
import { Input, Select, Button, Typography, Form, Space, MenuItem, Modal } from "antd";
import { DeleteOutlined, PlusOutlined, CloseOutlined } from "@ant-design/icons";
import cancelIcon from "../../app/assets/svgComponent/cancel.svg";
import saveIcon from "../../app/assets/svgComponent/save.svg";
import apiCall from "../../app/API/apiService";
import apiEndpoints from "../../app/API/apiEndpoints";

const { Option } = Select;
const { TextArea } = Input;

const TableForm = ({
	item,
	organisations = [],
	departments,
	subDepartments,
	roles,
	onChange,
	onSave,
	onClear,
	isEdit,
	onRemoveRound,
	onCriteriaChange,
	onAddCriteria,
	onRemoveCriteria,
}) => {
	console.log(item, "rolesrolesrolesroles===>");

	const [isModalVisible, setIsModalVisible] = React.useState(false);
	const [newCriteriaKey, setNewCriteriaKey] = React.useState('');
	
	// State for dynamic dropdown data
	const [localDepartments, setLocalDepartments] = useState([]);
	const [localSubDepartments, setLocalSubDepartments] = useState([]);
	const [localRoles, setLocalRoles] = useState([]);
	const [isLoadingDept, setIsLoadingDept] = useState(false);
	const [isLoadingSubDept, setIsLoadingSubDept] = useState(false);
	const [isLoadingRole, setIsLoadingRole] = useState(false);
	// Add API call tracking refs to prevent duplicate calls
	const orgApiCallInProgress = React.useRef(false);
	const deptApiCallInProgress = React.useRef(false);
	const subDeptApiCallInProgress = React.useRef(false);
	const roleApiCallInProgress = React.useRef(false);

	// Add a new state for error message
	const [weightErrorMessage, setWeightErrorMessage] = React.useState("");

	// Load organizations when creating a new job opening
	useEffect(() => {
		if (!isEdit && !item.org_id && !orgApiCallInProgress.current) {
			fetchOrganisations();
		}
	}, [isEdit, item.org_id]);

	// Fetch organizations
	const fetchOrganisations = async () => {
		if (orgApiCallInProgress.current) return;
		try {
			orgApiCallInProgress.current = true;
			const response = await apiCall("get", apiEndpoints.ORGANIZATION_SUMMARY);
			if (response && Array.isArray(response)) {
				// Update only in parent component
				onChange({
					target: {
						name: "organisations",
						value: response
					}
				});
			}
		} catch (error) {
			console.error("Error fetching organizations:", error);
		} finally {
			orgApiCallInProgress.current = false;
		}
	};

	// Fetch departments based on selected organization
	const fetchDepartments = async (orgId) => {
		if (!orgId || deptApiCallInProgress.current) return;
		
		try {
			setIsLoadingDept(true);
			deptApiCallInProgress.current = true;
			console.log("Fetching departments for org:", orgId);
			
			const response = await apiCall("get", `${apiEndpoints.DEPARTMENT_SUMMARY}?organisation_id=${orgId}`);
			if (response && Array.isArray(response)) {
				setLocalDepartments(response);
			}
		} catch (error) {
			console.error("Error fetching departments:", error);
		} finally {
			setIsLoadingDept(false);
			deptApiCallInProgress.current = false;
		}
	};

	// Fetch subdepartments based on selected department
	const fetchSubDepartments = async (orgId, deptId) => {
		if (!orgId || !deptId || subDeptApiCallInProgress.current) return;
		
		try {
			setIsLoadingSubDept(true);
			subDeptApiCallInProgress.current = true;
			console.log("Fetching subdepartments for org:", orgId, "dept:", deptId);
			
			const response = await apiCall("get", `${apiEndpoints.SUBDEPARTMENT_SUMMARY}?organisation_id=${orgId}&department_id=${deptId}`);
			if (response && Array.isArray(response)) {
				setLocalSubDepartments(response);
				console.log("Subdepartments fetched:", response);
			}
		} catch (error) {
			console.error("Error fetching subdepartments:", error);
		} finally {
			setIsLoadingSubDept(false);
			subDeptApiCallInProgress.current = false;
		}
	};

	// Fetch roles based on selected subdepartment
	const fetchRoles = async (orgId, deptId, subDeptId) => {
		if (!orgId || !deptId || !subDeptId || roleApiCallInProgress.current) {
			console.error("Cannot fetch roles - missing required params or API in progress:", {
				orgId,
				deptId,
				subDeptId,
				apiInProgress: roleApiCallInProgress.current
			});
			return;
		}
		
		try {
			setIsLoadingRole(true);
			roleApiCallInProgress.current = true;
			console.log("Fetching roles with params:", {
				orgId,
				deptId,
				subDeptId
			});
			
			const response = await apiCall(
				"get", 
				`${apiEndpoints.ROLE_SUMMARY}?org_id=${orgId}&department_id=${deptId}&subdepartment_id=${subDeptId}`
			);
			
			console.log("Roles API response:", response);
			
			if (response && Array.isArray(response)) {
				setLocalRoles(response);
			} else {
				console.error("Invalid roles response format:", response);
			}
		} catch (error) {
			console.error("Error fetching roles:", error);
		} finally {
			setIsLoadingRole(false);
			roleApiCallInProgress.current = false;
		}
	};

	// Function to fetch job openings based on selected filters
	const fetchJobOpenings = async () => {
		try {
			const selectedOrg = organisations.find(org => org.name === item.org_id);
			
			// Construct query parameters from selected filters
			const params = new URLSearchParams();
			if (selectedOrg) params.append('organisation_name', selectedOrg.name);
			if (item.department_name) params.append('department_name', item.department_name);
			if (item.subdepartment_name) params.append('subdepartment_name', item.subdepartment_name);
			if (item.role_name) params.append('role_name', item.role_name);
			
			const queryString = params.toString();
			const endpoint = `${apiEndpoints.JOB_OPENING}?${queryString}`;
			
			console.log("Fetching job openings with query:", endpoint);
			const response = await apiCall("get", endpoint);
			
			if (response) {
				console.log("Job openings response:", response);
				// Process response as needed
				return response;
			}
		} catch (error) {
			console.error("Error fetching job openings:", error);
		}
	};

	// Modify the onSave function to call fetchJobOpenings before saving
	const handleSave = () => {
		// Call the original onSave function directly
		onSave();
	};

	// Determine whether to use local (API fetched) data or prop data
	const displayDepartments = useMemo(() => {
		// Use local departments for new items, props for editing
		return isEdit ? departments : localDepartments;
	}, [isEdit, departments, localDepartments]);

	const displaySubDepartments = useMemo(() => {
		return isEdit ? subDepartments : localSubDepartments;
	}, [isEdit, subDepartments, localSubDepartments]);

	const displayRoles = useMemo(() => {
		return isEdit ? roles : localRoles;
	}, [isEdit, roles, localRoles]);

	// Updated memoized functions to use the display data
	const filteredDepartments = useMemo(() => {
		if (item?.org_id) {
			return displayDepartments;
		}
		return [];
	}, [item?.org_id, displayDepartments]);

	const filteredSubDepartments = useMemo(() => {
		console.log("Filtering subdepartments - department_id:", item?.department_id);
		console.log("Available subdepartments:", displaySubDepartments);
		if (item?.department_id) {
			return displaySubDepartments;
		}
		return [];
	}, [item?.department_id, displaySubDepartments]);

	const filteredRoles = useMemo(() => {
		if (item?.subdepartment_id) {
			return displayRoles;
		}
		return [];
	}, [item?.subdepartment_id, displayRoles]);

	// Memoized onChange handler - debounced to prevent multiple rapid calls
	const handleChange = useCallback(
		(event) => {
			const { name, value } = event.target;
			
			if (name === "org_id") {
				const selectedOrg = organisations.find((org) => org.name === value);
				
				// First set the org_id explicitly 
				if (selectedOrg) {
					onChange({
						target: {
							name: "org_id",
							value: selectedOrg.id
						}
					});
				}
				
				// Then set the org name
				onChange({
					target: {
						name,
						value
					},
				});
				
				// Reset dependent fields when organization changes
				onChange({
					target: {
						name: "department_name",
						value: "",
					},
				});
				onChange({
					target: {
						name: "department_id",
						value: null,
					},
				});
				onChange({
					target: {
						name: "subdepartment_name",
						value: "",
					},
				});
				onChange({
					target: {
						name: "subdepartment_id",
						value: null,
					},
				});
				onChange({
					target: {
						name: "role_name",
						value: "",
					},
				});
				onChange({
					target: {
						name: "role_id",
						value: null,
					},
				});
				
				// Clear cached data for dependent fields
				setLocalSubDepartments([]);
				setLocalRoles([]);
				
				// Fetch departments for the selected organization
				if (selectedOrg?.id && !deptApiCallInProgress.current) {
					// Reset API call flags for dependent fields
					subDeptApiCallInProgress.current = false;
					roleApiCallInProgress.current = false;
					
					// Fetch departments
					fetchDepartments(selectedOrg.id);
				}
			}
			else if (name === "department_name") {
				const selectedDept = displayDepartments.find((dept) => dept.name === value);
				console.log("Department selected:", value);
				console.log("Found department:", selectedDept);
				console.log("Department ID:", selectedDept ? selectedDept.id : null);
				
				// First set the department_id explicitly
				if (selectedDept) {
					onChange({
						target: {
							name: "department_id",
							value: selectedDept.id
						}
					});
				}
				
				// Then set the department_name
				onChange({
					target: {
						name,
						value
					},
				});
				
				// Reset dependent fields when department changes
				onChange({
					target: {
						name: "subdepartment_name",
						value: "",
					},
				});
				onChange({
					target: {
						name: "role_name",
						value: "",
					},
				});
				
				// Clear cached data for dependent fields
				setLocalRoles([]);
				
				// Fetch subdepartments if we have all required fields
				if (selectedDept?.id && !subDeptApiCallInProgress.current) {
					const selectedOrg = organisations.find(org => org.name === item.org_id);
					if (selectedOrg?.id) {
						// Reset API call flag for dependent field
						roleApiCallInProgress.current = false;
						
						// Fetch subdepartments
						fetchSubDepartments(selectedOrg.id, selectedDept.id);
					}
				}
			} 
			else if (name === "subdepartment_name") {
				const selectedSubDept = filteredSubDepartments.find(
					(subDept) => subDept.name === value
				);
				
				console.log("Selected SubDepartment:", selectedSubDept);
				
				// First set the subdepartment_id explicitly
				if (selectedSubDept) {
					onChange({
						target: {
							name: "subdepartment_id",
							value: selectedSubDept.id
						}
					});
				}
				
				// Then set the subdepartment name
				onChange({
					target: {
						name,
						value
					},
				});
				
				// Reset role when subdepartment changes
				onChange({
					target: {
						name: "role_name",
						value: "",
					},
				});
				onChange({
					target: {
						name: "role_id",
						value: null,
					},
				});
				
				// Fetch roles if we have all required fields
				if (selectedSubDept?.id && !roleApiCallInProgress.current) {
					const selectedOrg = organisations.find(org => org.name === item.org_id);
					console.log("Selected Org:", selectedOrg);
					console.log("Current item state:", {
						org_id: item.org_id,
						department_id: item.department_id,
						subdepartment_id: selectedSubDept.id
					});
					
					if (selectedOrg?.id && item.department_id) {
						console.log("Calling fetchRoles with:", {
							orgId: selectedOrg.id,
							deptId: item.department_id,
							subDeptId: selectedSubDept.id
						});
						fetchRoles(selectedOrg.id, item.department_id, selectedSubDept.id);
					} else {
						console.error("Missing required IDs for role fetch:", {
							orgId: selectedOrg?.id,
							deptId: item.department_id,
							subDeptId: selectedSubDept?.id
						});
					}
				}
			} 
			else if (name === "role_name") {
				const selectedRole = filteredRoles.find((role) => role.name === value || role.role_name === value);
				
				console.log("Role selected:", value);
				console.log("Found role:", selectedRole);
				console.log("Role ID:", selectedRole ? selectedRole.id : null);
				
				// First set the role_id explicitly
				if (selectedRole) {
					onChange({
						target: {
							name: "role_id",
							value: selectedRole.id
						}
					});
				}
				
				// Then set the role name
				onChange({
					target: {
						name,
						value
					},
				});
			} 
			else {
				onChange(event);
			}
		},
		[
			organisations,
			displayDepartments,
			filteredSubDepartments,
			filteredRoles,
			onChange,
			item?.org_id,
			item?.department_id,
		]
	);

	// Updated handleRoleChange function to set level field
	const handleRoleChange = useCallback(async(event) => {
		const { value } = event.target;
		
		console.log(filteredRoles, "filteredRoles===>");
		const selectedRole = filteredRoles.find((role) => role.name === value || role.role_name === value);
		
		console.log("Role change handler - selected:", value);
		console.log("Found role:", selectedRole);

		if (selectedRole) {
			try {
				const originalData = await apiCall("get", `${apiEndpoints.ROLE}/${selectedRole.id}`);
				console.log("Original Role Data:", originalData);

				// Save all the original data into item state
				onChange({
					target: {
						name: "original_role_data",
						value: originalData
					}
				});

				// Set role_id
				onChange({
					target: {
						name: "role_id",
						value: selectedRole.id
					}
				});
				
				// Set level from original data
				if (originalData.level !== undefined) {
					onChange({
						target: {
							name: "level",
							value: String(originalData.level)
						}
					});
				}
				
				// Set employment_type from original data
				if (originalData.employment_type) {
					onChange({
						target: {
							name: "employment_type",
							value: originalData.employment_type
						}
					});
				}
				
				// Set designation if available
				if (selectedRole.role_name || selectedRole.designation) {
					onChange({
						target: {
							name: "designation",
							value: selectedRole.role_name || selectedRole.designation || ""
						}
					});
				}
				
				// Set salary_min from original data
				if (originalData.salary_min !== undefined) {
					onChange({
						target: {
							name: 'salary_min',
							value: originalData.salary_min,
						}
					});
				}

				// Set salary_max from original data
				if (originalData.salary_max !== undefined) {
					onChange({
						target: {
							name: 'salary_max',
							value: originalData.salary_max,
						}
					});
				}

				// Set work_arrangement from original data
				if (originalData.work_arrangement) {
					onChange({
						target: {
							name: 'work_arrangement',
							value: originalData.work_arrangement,
						}
					});
				}

				// Set location_id from original data
				if (originalData.location_id) {
					onChange({
						target: {
							name: 'location_id',
							value: originalData.location_id,
						}
					});
				}
				
				// Auto-fill total_rounds from the selected role if available
				if (originalData.total_rounds) {
					onChange({
						target: {
							name: 'total_rounds',
							value: originalData.total_rounds,
						}
					});
				}

				// Set any other fields from originalData that you want to preserve
				if (originalData.evaluation_criteria) {
					onChange({
						target: {
							name: 'evaluation_criteria',
							value: originalData.evaluation_criteria,
						}
					});
				}

				if (originalData.opening_rounds) {
					onChange({
						target: {
							name: 'opening_rounds',
							value: originalData.opening_rounds,
						}
					});
				}
			} catch (error) {
				console.error("Error fetching role details:", error);
			}
		}
		
		// Set role_name
		onChange({
			target: {
				name: 'role_name',
				value
			}
		});
	}, [filteredRoles, onChange]);

	// Determine when fields should be enabled
	const isDepartmentDisable = !item?.org_id;
	const isSubDepartmentDisabled = !item?.department_id;
	const isRoleDisabled = !item?.subdepartment_id;
	const areOtherFieldsDisabled = !item?.role_name;

	// Define evaluationCriteriaValidation before areAllRequiredFieldsFilled
	const evaluationCriteriaValidation = useMemo(() => {
		console.log('Evaluation Criteria State:', {
			criteria: item?.evaluation_criteria,
			criteriaJSON: JSON.stringify(item?.evaluation_criteria)
		});
		
		if (!item?.evaluation_criteria?.length) {
			return { isValid: false, message: "Add at least one criteria" };
		}
		
		// Check if any weight is empty
		const hasEmptyWeight = item.evaluation_criteria.some(criteria => {
			const isEmpty = criteria.weight === "" || 
							criteria.weight === null || 
							criteria.weight === undefined;
			console.log('Criteria weight:', criteria.weight, 'isEmpty:', isEmpty);
			return isEmpty;
		});

		console.log('Has empty weight:', hasEmptyWeight);

		if (hasEmptyWeight) {
			return { isValid: true, message: "" };
		}

		const weightSum = item.evaluation_criteria.reduce((sum, criteria) => {
			const weight = Number(criteria.weight) || 0;
			console.log('Adding weight:', weight, 'to sum:', sum);
			return sum + weight;
		}, 0);
		
		console.log('Final weight sum:', weightSum);

		if (Math.abs(weightSum - 1.0) > 0.001) {
			return { 
				isValid: false, 
				message: `Sum of weights must be 1.0 (current: ${weightSum.toFixed(2)})`
			};
		}

		return { isValid: true, message: "" };
	}, [item?.evaluation_criteria]);

	// Now define areAllRequiredFieldsFilled
	const areAllRequiredFieldsFilled = useMemo(() => {
		const hasValidTotalRounds = item?.total_rounds && 
			item.total_rounds > 0 && 
			(!item.opening_rounds || item.opening_rounds.length <= item.total_rounds);

		// For evaluation criteria, only check if there's at least one criteria
		const hasEvaluationCriteria = item?.evaluation_criteria?.length > 0;

		return (
			item?.jobopening_name &&
			item?.org_id &&
			item?.department_name &&
			item?.subdepartment_name &&
			item?.role_name &&
			hasValidTotalRounds &&
			item?.no_open_positions &&
			item?.salary_min &&
			item?.salary_max &&
			item?.employment_type &&
			item?.work_arrangement &&
			item?.location_id &&
			item?.job_description &&
			hasEvaluationCriteria
			// Removed evaluationCriteriaValidation.isValid check
		);
	}, [item, evaluationCriteriaValidation]);

	const handlePromptChange = (e) => {
		const { name, value } = e.target;
		onChange({ target: { name, value } });
	};

	// const handleRoleSelection = (roleId) => {
	// 	getRoleByIdData(roleId);
	// };

	// Update the initial round structure when adding a new round
	const onAddRound = () => {
		const newRound = {
			round_name: "",
			type: "",
			prompt: "",
			objective: "",
			evaluation_criteria: "",
			no_questions_ask: 0,
			interview_category: "TECH", // Set default value
			question_bank: [],
			code_language: "ALL"
		};
		
		// Add the new round to the existing rounds
		const updatedRounds = [...(item.opening_rounds || []), newRound];
		onChange({
			target: {
				name: "opening_rounds",
				value: updatedRounds
			}
		});
	};

	// Ensure this function is declared only once
	const onRoundChange = (roundIndex, field, value) => {
		const updatedRounds = [...item.opening_rounds];
		updatedRounds[roundIndex] = {
			...updatedRounds[roundIndex],
			[field]: value // Update the specific field
		};
		
		console.log(`Updating round ${roundIndex}, field: ${field}, value: ${value}`);
		
		onChange({
			target: {
				name: "opening_rounds",
				value: updatedRounds
			}
		});
	};

	const inputStyle = {
		width: "368px",
		height: "34px",
		padding: "6px 10px",
		gap: "8px",
		borderRadius: "6px",
		border: "1px solid rgba(255, 255, 255, 1)",
		boxShadow: "0px 1px 2px 0px rgba(17, 24, 39, 0.08), 0px 0px 0px 1px rgba(17, 24, 39, 0.1)",
		backgroundColor: "#F3F4F6",
		fontSize: "14px",
		fontFamily: "Inter"
	};

	const selectStyle = {
		...inputStyle,
		'.ant-select-selector': {
			backgroundColor: 'transparent !important',
			border: 'none !important',
			boxShadow: 'none !important',
		}
	};

	// Add this to override Ant Design's default select styles
	const selectProps = {
		popupClassName: "custom-select-dropdown",
		dropdownStyle: { 
			backgroundColor: '#F3F4F6',
			borderRadius: '6px',
		},
		dropdownMatchSelectWidth: true
	};

	const labelStyle = {
		display: "block",
		fontSize: "14px",
		color: "#374151",
		marginBottom: "8px",
		fontWeight: "500"
	};

	// Update the resetItem function to include predefined evaluation criteria
	const resetItem = () => {
		return {
			// ... other existing fields ...
			evaluation_criteria: [
				{ key: "Pedigree Education", description: "", weight: "" },
				{ key: "Relevant Experience", description: "", weight: "" },
				{ key: "Years of Experience", description: "", weight: "" },
				{ key: "Career Progression", description: "", weight: "" },
				{ key: "Frequent Hops", description: "", weight: "" },
				{ key: "Scope of Expertise", description: "", weight: "" },
				{ key: "Level of Proficiency", description: "", weight: "" },
				{ key: "Previous Companies", description: "", weight: "" },
				{ key: "Download of app / scale", description: "", weight: "" },
				{ key: "Certifications", description: "", weight: "" },
				{ key: "Extracurricular Activities", description: "", weight: "" }
			],
			// ... other existing fields ...
		};
	};

	const handleCriteriaChange = (criteriaIndex, field, value, isNew = false) => {
		const updatedItem = { ...item };
		
		if (isNew) {
			// Adding a new custom criteria
			if (!updatedItem.evaluation_criteria) {
				updatedItem.evaluation_criteria = [];
			}
			updatedItem.evaluation_criteria.push({
				key: value,
				description: "",
				weight: "",
				isDefault: false // Mark as custom criteria
			});
		} else {
			// Updating existing criteria
			if (!updatedItem.evaluation_criteria[criteriaIndex]) {
				updatedItem.evaluation_criteria[criteriaIndex] = { 
					description: "", 
					weight: 0.0,
					isDefault: false
				};
			}
			updatedItem.evaluation_criteria[criteriaIndex][field] = value;
		}
		
		onChange({
			target: {
				name: "evaluation_criteria",
				value: updatedItem.evaluation_criteria
			}
		});
	};

	const handleModalOk = () => {
		if (newCriteriaKey.trim()) {
			onCriteriaChange(
				item.evaluation_criteria.length,
				'key',
				newCriteriaKey.trim(),
				true // isNew flag
			);
			setNewCriteriaKey('');
			setIsModalVisible(false);
		}
	};

	const handleModalCancel = () => {
		setNewCriteriaKey('');
		setIsModalVisible(false);
	};

	// Modified isFormValid function to handle API loading state
	const isFormValid = () => {
		return areAllRequiredFieldsFilled;
	};

	const validateWeight = (criteriaIndex, newWeight) => {
		// Reset error message
		setWeightErrorMessage("");
		
		// Skip validation for empty values
		if (newWeight === "" || newWeight === null || newWeight === undefined) {
			return true;
		}
		
		// Convert to number and validate format
		const numWeight = Number(newWeight);
		if (isNaN(numWeight) || numWeight < 0) {
			return false;
		}
		
		// Calculate sum of all other weights
		const otherWeightsSum = item.evaluation_criteria.reduce((sum, criteria, index) => {
			if (index === criteriaIndex) return sum; // Skip current criteria
			return sum + (Number(criteria.weight) || 0);
		}, 0);
		
		// Check if adding this weight would exceed 1.0
		const isValid = (otherWeightsSum + numWeight) <= 1.0;
		
		// Set error message if invalid
		if (!isValid) {
			setWeightErrorMessage(`Weight sum cannot exceed 1.0 (current sum: ${(otherWeightsSum + numWeight).toFixed(2)})`);
		}
		
		return isValid;
	};

	return (
		<>
			<Modal
				title="Add Custom Evaluation Criteria"
				open={isModalVisible}
				onOk={handleModalOk}
				onCancel={handleModalCancel}
				okButtonProps={{ 
					style: { 
						backgroundColor: '#2563EB',
						borderColor: '#2563EB'
					}
				}}
			>
				<div style={{ marginBottom: "16px" }}>
					<label style={labelStyle}>Criteria Name</label>
					<Input
						value={newCriteriaKey}
						onChange={(e) => setNewCriteriaKey(e.target.value)}
						placeholder="Enter criteria name"
						style={inputStyle}
						autoFocus
					/>
				</div>
			</Modal>

			<div style={{ 
				width: "410px",
				height: "88vh",
				fontFamily: "inter",
				padding: "0",
				borderRadius: "12px",
				backgroundColor: "#FFFFFF",
				position: "fixed",
				right: "12px",
				top: "12px",
				boxShadow: `
					0px 20px 44px 0px rgba(0, 0, 0, 0.1),
					0px 80px 80px 0px rgba(0, 0, 0, 0.09),
					0px 180px 108px 0px rgba(0, 0, 0, 0.05),
					0px 319px 128px 0px rgba(0, 0, 0, 0.01),
					0px 499px 140px 0px rgba(0, 0, 0, 0)
				`,
				boxSizing: "border-box",
				display: "flex", 
				flexDirection: "column",
				overflow: "hidden"
			}}>
				{/* Header */}
				<div style={{
					width: "100%",
					height: "48px",
					padding: "8px 16px",
					borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					background: "rgba(247, 247, 248, 1)",
					flexShrink: 0
				}}>
					<div style={{ 
						display: "flex", 
						alignItems: "center", 
						gap: "6px"
					}}>
						<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M7.00156 4.19961H2.10156V3.14961C2.10156 2.57001 2.57196 2.09961 3.15156 2.09961H5.22706C5.46086 2.09961 5.67996 2.21651 5.80946 2.41111L7.00156 4.19961Z" fill="#2563EB" stroke="#2563EB" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
							<path d="M2.10156 4.19971H10.8516C11.4312 4.19971 11.9016 4.67011 11.9016 5.24971V9.79971C11.9016 10.9589 10.9608 11.8997 9.80156 11.8997H4.20156C3.04236 11.8997 2.10156 10.9589 2.10156 9.79971V4.19971Z" stroke="#2563EB" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
						</svg>
						<span style={{ 
							fontWeight: "500",
							fontSize: "16px"
						}}>
							{isEdit ? 'Edit job opening' : 'Create new job opening'}
						</span>
					</div>
				</div>

				{/* Form Content */}
				<div style={{ 
					padding: "24px 12px 24px 24px", 
					width: "100%",
					flex: 1,
					overflowY: "auto",
					overflowX: "hidden"
				}}>
					{/* Interviewer Selection */}
					<div style={{ marginBottom: "24px" }}>
						<label style={labelStyle}>Interviewer <span style={{ color: 'red' }}>*</span></label>
						<Select
							name="interviewer"
							value={item?.interviewer || "SARAH"}
							onChange={(value) => handleChange({ target: { name: "interviewer", value }})}
							style={selectStyle}
							{...selectProps}
						>
							<Option value="SARAH"></Option>
							<Option value="RIYA">RIYA</Option>
							<Option value="ANJALI">ANJALI</Option>
						</Select>
					</div>

					{/* Organization Selection */}
					<div style={{ marginBottom: "24px" }}>
						<label style={labelStyle}>Organization <span style={{ color: 'red' }}>*</span></label>
						<Select
							name="org_id"
							value={item?.org_id || ""}
							onChange={(value) => handleChange({ target: { name: "org_id", value }})}
							disabled={isEdit}
							displayEmpty
							style={selectStyle}
							{...selectProps}
							loading={!organisations.length}
						>
							<Option value="" disabled>
								<span style={{ color: '#A9A9A9', fontSize: '14px' }}>choose from this list</span>
							</Option>
							{organisations.map((org) => (
								<Option key={org.id} value={org.name}>{org.name}</Option>
							))}
						</Select>
					</div>

					{/* Department Dropdown */}
					<div style={{ marginBottom: "24px" }}>
						<label style={labelStyle}>Department <span style={{ color: 'red' }}>*</span></label>
						<Select
							name="department_name"
							value={item?.department_name || ""}
							onChange={(value) => handleChange({ target: { name: "department_name", value }})}
							disabled={isEdit || isDepartmentDisable}
							displayEmpty
							style={selectStyle}
							{...selectProps}
							loading={isLoadingDept}
						>
							<Option value="" disabled>
								<span style={{ color: '#A9A9A9', fontSize: '14px' }}>choose from this list</span>
							</Option>
							{filteredDepartments.map((department) => (
								<Option key={department.id} value={department.name}>
									{department.name}
								</Option>
							))}
						</Select>
					</div>
					
					{/* Subdepartment Dropdown */}
					<div style={{ marginBottom: "24px" }}>
						<label style={labelStyle}>Sub-Department <span style={{ color: 'red' }}>*</span></label>
						<Select
							name="subdepartment_name"
							value={item?.subdepartment_name || ""}
							onChange={(value) => handleChange({ target: { name: "subdepartment_name", value }})}
							disabled={isEdit || isSubDepartmentDisabled}
							displayEmpty
							style={selectStyle}
							{...selectProps}
							loading={isLoadingSubDept}
						>
							<Option value="" disabled>
								<span style={{ color: '#A9A9A9', fontSize: '14px' }}>choose from this list</span>
							</Option>
							{filteredSubDepartments.map((subDept) => (
								<Option key={subDept.id} value={subDept.name}>
									{subDept.name}
								</Option>
							))}
						</Select>
					</div>

					{/* Role Dropdown */}
					<div style={{ marginBottom: "24px" }}>
						<label style={labelStyle}>Role <span style={{ color: 'red' }}>*</span></label>
						<Select
							name="role_name"
							value={item?.role_name || ""}
							onChange={(value) => handleRoleChange({ target: { name: "role_name", value }})}
							disabled={isEdit || isRoleDisabled}
							style={selectStyle}
							{...selectProps}
							loading={isLoadingRole}
						>
							<Option value="" disabled>
								<span style={{ color: '#A9A9A9', fontSize: '14px' }}>choose from this list</span>
							</Option>
							{filteredRoles.map((role) => (
								<Option key={role.id} value={role.name || role.role_name}>
									{role.name || role.role_name}
								</Option>
							))}
						</Select>
					</div>

					<div style={{ marginBottom: "24px" }}>
						<label style={labelStyle}>Total Rounds <span style={{ color: 'red' }}>*</span></label>
						<Input
							name="total_rounds"
							type="number"
							value={item?.total_rounds || ""}
							onChange={handleChange}
							disabled={areOtherFieldsDisabled && !isEdit}
							min={1}
							style={inputStyle}
						/>
						{item?.opening_rounds?.length > item?.total_rounds && (
							<Typography.Text type="danger" style={{ whiteSpace: 'nowrap' }}>
								Total rounds cannot be less than the number of rounds added
							</Typography.Text>
						)}
					</div>

					<div style={{ marginBottom: "24px" }}>
						<label style={labelStyle}>Job Opening Name <span style={{ color: 'red' }}>*</span></label>
						<Input
							name="jobopening_name"
							value={item?.jobopening_name || ""}
							onChange={handleChange}
							disabled={areOtherFieldsDisabled && !isEdit}
							style={inputStyle}
						/>
					</div>

					<div style={{ marginBottom: "24px" }}>
						<label style={labelStyle}>No of Positions <span style={{ color: 'red' }}>*</span></label>
						<Input
							name="no_open_positions"
							type="number"
							value={item?.no_open_positions < 0 ? 0 : item?.no_open_positions || ""}
							onChange={(event) => {
								const value = Math.max(0, event.target.value); // Ensure value is not negative
								handleChange({
									target: {
										name: "no_open_positions",
										value: value,
									},
								});
							}}
							style={inputStyle}
						/>
					</div>

					<div style={{ 
						marginBottom: "24px",
						display: "flex",
						gap: "16px",
						overflow: "hidden"
					}}>
						<div style={{ flex: 1 }}>
							<label style={labelStyle}>Salary Min <span style={{ color: 'red' }}>*</span></label>
							<Input
								name="salary_min"
								type="number"
								value={item?.salary_min || ""}
								onChange={handleChange}
								style={{ ...inputStyle, width: "80%" }}
								placeholder="x,xx,xxx"
							/>
						</div>
						<div style={{ flex: 1 }}>
							<label style={labelStyle}>Salary Max <span style={{ color: 'red' }}>*</span></label>
							<Input
								name="salary_max"
								type="number"
								value={item?.salary_max || ""}
								onChange={handleChange}
								style={{ ...inputStyle, width: "80%" }}
								placeholder="xx,xx,xxx"
							/>
						</div>
					</div>

					<div style={{ marginBottom: "24px" }}>
						<label style={labelStyle}>Employment Type <span style={{ color: 'red' }}>*</span></label>
						<Select
							name="employment_type"
							value={item?.employment_type || ""}
							onChange={(value) => onChange({ target: { name: "employment_type", value }})}
							displayEmpty
							style={selectStyle}
							{...selectProps}
						>
							<Option value="" disabled>
								<span style={{ color: '#A9A9A9', fontSize: '14px' }}>choose from this list</span>
							</Option>
							<Option value="FT">Full Time</Option>
							<Option value="PT">Part Time</Option>
						</Select>
					</div>

					<div style={{ marginBottom: "24px" }}>
						<label style={labelStyle}>Work Arrangement <span style={{ color: 'red' }}>*</span></label>
						<Select
							name="work_arrangement"
							value={item?.work_arrangement || ""}
							onChange={(value) => onChange({ target: { name: "work_arrangement", value }})}
							displayEmpty
							style={selectStyle}
							{...selectProps}
						>
							<Option value="" disabled>
								<span style={{ color: '#A9A9A9', fontSize: '14px' }}>choose from this list</span>
							</Option>
							<Option value="WFO">Work From Office</Option>
							<Option value="WFH">Work From Home</Option>
							<Option value="Hybrid">Hybrid</Option>
						</Select>
					</div>

					<div style={{ marginBottom: "24px" }}>
						<label style={labelStyle}>City <span style={{ color: 'red' }}>*</span></label>
						<Input
							name="location_id"
							value={item?.location_id || ""}
							onChange={handleChange}
							style={inputStyle}
						/>
					</div>

					<div style={{ marginBottom: "24px" }}>
						<label style={labelStyle}>Job Description <span style={{ color: 'red' }}>*</span></label>
						<TextArea
							name="job_description"
							value={item?.job_description || ""}
							onChange={handleChange}
							style={inputStyle}
						/>
					</div>

					{/* Evaluation Criteria Section */}
					<div style={{ marginBottom: "24px" }}>
						<label style={labelStyle}>Evaluation Criteria <span style={{ color: 'red' }}>*</span></label>
						{!evaluationCriteriaValidation.isValid && (
							<Typography.Text type="danger">
								{evaluationCriteriaValidation.message}
							</Typography.Text>
						)}
						{weightErrorMessage && (
							<Typography.Text type="danger" style={{ display: 'block', marginBottom: '8px' }}>
								{weightErrorMessage}
							</Typography.Text>
						)}
						
						<div style={{ 
							display: 'grid', 
							gridTemplateColumns: '368px', // Single column with fixed width
							gap: '16px',
							marginTop: '8px'
						}}>
							{item?.evaluation_criteria?.map((criteria, criteriaIndex) => (
								<div key={criteriaIndex} style={{
									padding: '16px',
									border: '1px solid #E5E7EB',
									borderRadius: '8px',
									backgroundColor: '#FFFFFF',
									position: 'relative'
								}}>
									{/* Show delete button only for custom criteria */}
									{!criteria.isDefault && (
										<Button
											type="text"
											danger
											icon={<DeleteOutlined />}
											onClick={() => onRemoveCriteria(criteriaIndex)}
											style={{
												position: 'absolute',
												right: '8px',
												top: '8px'
											}}
										/>
									)}

									{/* Key */}
									<div style={{
										fontSize: '14px',
										fontWeight: '500',
										color: '#374151',
										marginBottom: '8px'
									}}>
										{criteria.key}
									</div>
									
									{/* Description */}
									<TextArea
										placeholder="Description"
										value={criteria.description}
										onChange={(e) => onCriteriaChange(criteriaIndex, 'description', e.target.value)}
										style={{ 
											...inputStyle,
											marginBottom: '8px',
											width: '100%',
											height: '80px'
										}}
									/>
									
									{/* Weight */}
									<Input
										type="number"
										placeholder="Weight"
										value={criteria.weight}
										onChange={(e) => {
											const newWeight = e.target.value;
											if (validateWeight(criteriaIndex, newWeight)) {
												onCriteriaChange(criteriaIndex, 'weight', newWeight);
											} else {
												// Optionally show a message or toast here
												console.warn("Weight sum cannot exceed 1.0");
											}
										}}
										style={{ 
											...inputStyle,
											width: '100%'
										}}
										min={0}
										max={1}
										step={0.1}
									/>
								</div>
							))}
						</div>

						{/* Moved and restyled Add Custom Criteria Button */}
						<Button
							type=""
							onClick={() => setIsModalVisible(true)}
							icon={<PlusOutlined />}
							style={{ 
								width: '90%',
								marginTop: '16px',
								background: 'var(--Dark-Blue-100, rgba(219, 230, 254, 1))',
								color: '#2563EB',
							}}
						>
							Add Custom Criteria
						</Button>
					</div>

					{/* Rounds Section */}
					{item?.opening_rounds?.map((round, roundIndex) => (
						<div key={roundIndex} style={{ 
							marginBottom: "24px",
							border: "1px solid #E5E7EB",
							borderRadius: "8px",
							padding: "16px",
							marginLeft: "-20px",
							width: "390px"
						}}>
							<Space direction="vertical" style={{ width: '100%' }}>
								<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
									<Typography.Title level={5}>Round {roundIndex + 1}</Typography.Title>
									<Button
										type="text"
										danger
										icon={<DeleteOutlined />}
										onClick={() => onRemoveRound(roundIndex)}
									>
										Delete Round
									</Button>
								</div>
								
								{/* Round Name Input */}
								<div style={{ marginBottom: "24px" }}>
									<label style={labelStyle}>Round Name <span style={{ color: 'red' }}>*</span></label>
									<Input
										placeholder="Round Name"
										value={round.round_name}
										onChange={(e) => onRoundChange(roundIndex, "round_name", e.target.value)}
										style={inputStyle}
									/>
								</div>
								
								{/* Round Type Select */}
								<div style={{ marginBottom: "24px" }}>
									<label style={labelStyle}>Interviewer Type <span style={{ color: 'red' }}>*</span></label>
									<Select
										value={round.type}
										onChange={(value) => onRoundChange(roundIndex, "type", value)}
										style={selectStyle}
										{...selectProps}
										placeholder="Select Type"
									>
										<Select.Option value="" disabled>
									<span style={{ color: '#A9A9A9', fontSize: '14px' }}>choose from this list</span>
								</Select.Option>
										<Option value="AI">AI</Option>
										<Option value="Human">Human</Option>
									</Select>
									
								</div>

								{/* Evaluation Criteria TextArea */}
								<div style={{ marginBottom: "24px" }}>
									<label style={labelStyle}>Evaluation Prompt <span style={{ color: 'red' }}>*</span></label>
									<TextArea
										placeholder="Evaluation Prompt"
										value={round.evaluation_criteria}
										onChange={(e) => onRoundChange(roundIndex, "evaluation_criteria", e.target.value)}
										style={inputStyle}
									/>
								</div>

								{/* Questions to be Asked Input */}
								<div style={{ marginBottom: "24px" }}>
									<label style={labelStyle}>Questions to be Asked <span style={{ color: 'red' }}>*</span></label>
									<Input
										type="number"
										placeholder="Questions to be Asked"
										value={round.no_questions_ask}
										onChange={(e) => onRoundChange(roundIndex, "no_questions_ask", parseInt(e.target.value) || 0)}
										min={0}
										style={inputStyle}
									/>
								</div>

								{/* Interview Category Select */}
								<div style={{ marginBottom: "24px" }}>
									<label style={labelStyle}>Interview Type <span style={{ color: 'red' }}>*</span></label>
									<Select
										value={round.interview_category}
										onChange={(value) => onRoundChange(roundIndex, "interview_category", value)|| "TECH"}
										style={selectStyle}
										{...selectProps}
										placeholder="Select Question Type"
									>
										<Option value="TECH">TECH</Option>
										<Option value="NON_TECH">NON_TECH</Option>
									</Select>
								</div>

								{/* Add the new Code Language Select */}
								<div style={{ marginBottom: "24px" }}>
									<label style={labelStyle}>Programming Language <span style={{ color: 'red' }}>*</span></label>
									<Select
										value={round.code_language || "ALL"}
										onChange={(value) => onRoundChange(roundIndex, "code_language", value)}
										style={selectStyle}
										{...selectProps}
										placeholder="Select Programming Language"
									>
										<Option value="PYTHON">PYTHON</Option>
										<Option value="CPP">C++</Option>
										<Option value="JAVA">JAVA</Option>
										<Option value="JAVASCRIPT">JAVASCRIPT</Option>
										<Option value="ALL">ALL</Option>
									</Select>
								</div>
							</Space>
						</div>
					))}

					{/* Add Round Button */}
					<Button
						type=""
						onClick={onAddRound}
						icon={<PlusOutlined />}
						style={{ 
							width: '90%', 
							marginBottom: "24px",
							background: 'var(--Dark-Blue-100, rgba(219, 230, 254, 1))',
							color: '#2563EB',
							
						}}
					>
						Add Round
					</Button>
				</div>

				{/* Footer */}
				<div style={{ 
					width: "100%", 
					height: "80px", 
					display: "flex",
					gap: "10px",
					padding: "16px",
					background: "rgba(247, 247, 248, 1)",
					borderTop: "1px solid rgba(0, 0, 0, 0.1)",
					flexShrink: 0,
					justifyContent: "center",
					alignItems: "center",
					borderRadius: "12px 12px 0 0"
				}}>
					<img 
						src={cancelIcon} 
						onClick={onClear}
						alt="cancel" 
						style={{ 
							width: "180px",
							height: "50px",
							objectFit: "contain",
							cursor: "pointer",
							transition: "transform 0.2s"
						}}
						onMouseDown={(e) => e.currentTarget.style.transform = "scale(0.95)"}
						onMouseUp={(e) => e.currentTarget.style.transform = "scale(1)"}
						onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
					/>
					
					<img 
						src={saveIcon} 
						onClick={isFormValid() ? handleSave : undefined}
						alt="save" 
						style={{ 
							width: "180px",
							height: "48px",
							objectFit: "contain",
							cursor: isFormValid() ? "pointer" : "not-allowed",
							transition: "transform 0.2s",
							opacity: isFormValid() ? 1 : 0.5
						}}
						onMouseDown={(e) => isFormValid() && (e.currentTarget.style.transform = "scale(0.95)")}
						onMouseUp={(e) => isFormValid() && (e.currentTarget.style.transform = "scale(1)")}
						onMouseLeave={(e) => isFormValid() && (e.currentTarget.style.transform = "scale(1)")}
					/>
				</div>
			</div>
		</>
	);
};

// Add this CSS to your stylesheet or in a style tag
const styleTag = document.createElement('style');
styleTag.textContent = `
	.ant-select-selector {
		background-color: transparent !important;
		border: none !important;
		box-shadow: none !important;
	}
	
	.custom-select-dropdown .ant-select-item {
		background-color: #F3F4F6 !important;
	}
	
	.custom-select-dropdown .ant-select-item-option-selected {
		background-color: #E5E7EB !important;
	}
	
	.custom-select-dropdown .ant-select-item-option-active {
		background-color: #E5E7EB !important;
	}

	.ant-checkbox {
		transform: scale(1); /* Adjust the scale as needed */
	}
	.ant-checkbox-inner {
		width: 10px; /* Set the width of the checkbox */
		height: 10px; /* Set the height of the checkbox */
	}
`;
document.head.appendChild(styleTag);

export default TableForm;